"use client";

import { c as _c } from "react/compiler-runtime";
import { type KeyboardEvent, useCallback, useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ZoomIn, ZoomOut, Info, View, CircleHelp } from "lucide-react";
import { HoverCard } from "radix-ui";
import styles from "./accessibility-controls.module.scss";
import ButtonComponent from "../ui/ButtonComponent";
import clsx from "clsx";

// Intervalo en ms para acción continua al mantener presionado
const HOLD_INTERVAL = 100;
export interface AccessibilityControlsLabels {
  panUp: string;
  panDown: string;
  panLeft: string;
  panRight: string;
  zoomIn: string;
  zoomOut: string;
  resetView: string;
  textualAlternative: string;
  info: string;
  actionsTitle: string;
  legendTitle: string;
}
export interface AccessibilityControlsProps {
  onPanUp: () => void;
  onPanDown: () => void;
  onPanLeft: () => void;
  onPanRight: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onTextualAlternative?: () => void;
  zoomPercentage?: number;
  labels: AccessibilityControlsLabels;
  legendContent?: React.ReactNode;
}

/**
 * Hook para acción continua al mantener presionado un botón.
 * Ejecuta la acción inmediatamente y luego cada HOLD_INTERVAL ms.
 */
function useHoldAction() {
  const $ = _c(3);
  const intervalRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = action => {
      action();
      intervalRef.current = setInterval(action, HOLD_INTERVAL);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const startHold = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const stopHold = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      startHold,
      stopHold
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/**
 * Controles de accesibilidad superpuestos sobre un mapa o globo 3D.
 * Renderiza flechas de navegación (arriba, abajo, izquierda, derecha),
 * controles de zoom (acercar, alejar, restablecer) y un botón de alternativa textual.
 */
export default function AccessibilityControls(t0) {
  const $ = _c(140);
  const {
    onPanUp,
    onPanDown,
    onPanLeft,
    onPanRight,
    onZoomIn,
    onZoomOut,
    onReset,
    onTextualAlternative,
    zoomPercentage,
    labels,
    legendContent
  } = t0;
  const holdUp = useHoldAction();
  const holdDown = useHoldAction();
  const holdLeft = useHoldAction();
  const holdRight = useHoldAction();
  const [infoPinned, setInfoPinned] = useState(false);
  const [infoHovered, setInfoHovered] = useState(false);
  const infoTriggerRef = useRef(null);
  const pointerOnTrigger = useRef(false);
  const infoOpen = infoPinned || infoHovered;
  const handleKeyboardAction = _temp;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = clsx(styles.arrowButton, styles.arrowUp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== holdUp || $[2] !== onPanUp) {
    t2 = () => holdUp.startHold(onPanUp);
    $[1] = holdUp;
    $[2] = onPanUp;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== holdUp || $[5] !== onPanUp) {
    t3 = () => holdUp.startHold(onPanUp);
    $[4] = holdUp;
    $[5] = onPanUp;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onPanUp) {
    t4 = event_0 => handleKeyboardAction(event_0, onPanUp);
    $[7] = onPanUp;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== holdUp.stopHold || $[10] !== labels.panUp || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <ButtonComponent className={t1} onMouseDown={t2} onMouseUp={holdUp.stopHold} onMouseLeave={holdUp.stopHold} onTouchStart={t3} onTouchEnd={holdUp.stopHold} onKeyDown={t4} aria-label={labels.panUp} title={labels.panUp} icon={ArrowUp} />;
    $[9] = holdUp.stopHold;
    $[10] = labels.panUp;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== holdDown || $[16] !== onPanDown) {
    t6 = () => holdDown.startHold(onPanDown);
    $[15] = holdDown;
    $[16] = onPanDown;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== holdDown || $[19] !== onPanDown) {
    t7 = () => holdDown.startHold(onPanDown);
    $[18] = holdDown;
    $[19] = onPanDown;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== onPanDown) {
    t8 = event_1 => handleKeyboardAction(event_1, onPanDown);
    $[21] = onPanDown;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== holdDown.stopHold || $[24] !== labels.panDown || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <ButtonComponent className={`${styles.arrowButton} ${styles.arrowDown}`} onMouseDown={t6} onMouseUp={holdDown.stopHold} onMouseLeave={holdDown.stopHold} onTouchStart={t7} onTouchEnd={holdDown.stopHold} onKeyDown={t8} aria-label={labels.panDown} title={labels.panDown} icon={ArrowDown} />;
    $[23] = holdDown.stopHold;
    $[24] = labels.panDown;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== holdLeft || $[30] !== onPanLeft) {
    t10 = () => holdLeft.startHold(onPanLeft);
    $[29] = holdLeft;
    $[30] = onPanLeft;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== holdLeft || $[33] !== onPanLeft) {
    t11 = () => holdLeft.startHold(onPanLeft);
    $[32] = holdLeft;
    $[33] = onPanLeft;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== onPanLeft) {
    t12 = event_2 => handleKeyboardAction(event_2, onPanLeft);
    $[35] = onPanLeft;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== holdLeft.stopHold || $[38] !== labels.panLeft || $[39] !== t10 || $[40] !== t11 || $[41] !== t12) {
    t13 = <ButtonComponent className={`${styles.arrowButton} ${styles.arrowLeft}`} onMouseDown={t10} onMouseUp={holdLeft.stopHold} onMouseLeave={holdLeft.stopHold} onTouchStart={t11} onTouchEnd={holdLeft.stopHold} onKeyDown={t12} aria-label={labels.panLeft} title={labels.panLeft} icon={ArrowLeft} />;
    $[37] = holdLeft.stopHold;
    $[38] = labels.panLeft;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== holdRight || $[44] !== onPanRight) {
    t14 = () => holdRight.startHold(onPanRight);
    $[43] = holdRight;
    $[44] = onPanRight;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== holdRight || $[47] !== onPanRight) {
    t15 = () => holdRight.startHold(onPanRight);
    $[46] = holdRight;
    $[47] = onPanRight;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== onPanRight) {
    t16 = event_3 => handleKeyboardAction(event_3, onPanRight);
    $[49] = onPanRight;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== holdRight.stopHold || $[52] !== labels.panRight || $[53] !== t14 || $[54] !== t15 || $[55] !== t16) {
    t17 = <ButtonComponent className={`${styles.arrowButton} ${styles.arrowRight}`} onMouseDown={t14} onMouseUp={holdRight.stopHold} onMouseLeave={holdRight.stopHold} onTouchStart={t15} onTouchEnd={holdRight.stopHold} onKeyDown={t16} aria-label={labels.panRight} title={labels.panRight} icon={ArrowRight} />;
    $[51] = holdRight.stopHold;
    $[52] = labels.panRight;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== infoPinned) {
    t18 = hovering => {
      if (pointerOnTrigger.current) {
        return;
      }
      if (!hovering && infoPinned) {
        return;
      }
      setInfoHovered(hovering);
    };
    $[57] = infoPinned;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  let t20;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      pointerOnTrigger.current = true;
    };
    t20 = () => {
      setInfoPinned(_temp2);
      requestAnimationFrame(() => {
        pointerOnTrigger.current = false;
      });
    };
    $[59] = t19;
    $[60] = t20;
  } else {
    t19 = $[59];
    t20 = $[60];
  }
  let t21;
  if ($[61] !== labels.info) {
    t21 = <HoverCard.Trigger asChild={true}><ButtonComponent ref={infoTriggerRef} aria-label={labels.info} title={labels.info} className={styles.infoTrigger} variant="plain" icon={CircleHelp} onPointerDown={t19} onClick={t20} /></HoverCard.Trigger>;
    $[61] = labels.info;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = e => {
      if (infoTriggerRef.current?.contains(e.target as Node)) {
        return;
      }
      setInfoPinned(false);
    };
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== labels.actionsTitle) {
    t23 = <p className={styles.infoSectionTitle}>{labels.actionsTitle}</p>;
    $[64] = labels.actionsTitle;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ArrowUp size={14} aria-hidden={true} />;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== labels.panUp) {
    t25 = <li className={styles.actionItem}>{t24} {labels.panUp}</li>;
    $[67] = labels.panUp;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ArrowDown size={14} aria-hidden={true} />;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== labels.panDown) {
    t27 = <li className={styles.actionItem}>{t26} {labels.panDown}</li>;
    $[70] = labels.panDown;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <ArrowLeft size={14} aria-hidden={true} />;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== labels.panLeft) {
    t29 = <li className={styles.actionItem}>{t28} {labels.panLeft}</li>;
    $[73] = labels.panLeft;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  let t30;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ArrowRight size={14} aria-hidden={true} />;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== labels.panRight) {
    t31 = <li className={styles.actionItem}>{t30} {labels.panRight}</li>;
    $[76] = labels.panRight;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <View size={14} aria-hidden={true} />;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== labels.resetView) {
    t33 = <li className={styles.actionItem}>{t32} {labels.resetView}</li>;
    $[79] = labels.resetView;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <ZoomIn size={14} aria-hidden={true} />;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== labels.zoomIn) {
    t35 = <li className={styles.actionItem}>{t34} {labels.zoomIn}</li>;
    $[82] = labels.zoomIn;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <ZoomOut size={14} aria-hidden={true} />;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== labels.zoomOut) {
    t37 = <li className={styles.actionItem}>{t36} {labels.zoomOut}</li>;
    $[85] = labels.zoomOut;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== t25 || $[88] !== t27 || $[89] !== t29 || $[90] !== t31 || $[91] !== t33 || $[92] !== t35 || $[93] !== t37) {
    t38 = <ul className={styles.actionsGrid}>{t25}{t27}{t29}{t31}{t33}{t35}{t37}</ul>;
    $[87] = t25;
    $[88] = t27;
    $[89] = t29;
    $[90] = t31;
    $[91] = t33;
    $[92] = t35;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== t23 || $[96] !== t38) {
    t39 = <div className={styles.infoSection}>{t23}{t38}</div>;
    $[95] = t23;
    $[96] = t38;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] !== labels.legendTitle || $[99] !== legendContent) {
    t40 = legendContent && <div className={styles.infoSection}><p className={styles.infoSectionTitle}>{labels.legendTitle}</p>{legendContent}</div>;
    $[98] = labels.legendTitle;
    $[99] = legendContent;
    $[100] = t40;
  } else {
    t40 = $[100];
  }
  let t41;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <HoverCard.Arrow className={styles.infoArrow} />;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  let t42;
  if ($[102] !== t39 || $[103] !== t40) {
    t42 = <HoverCard.Portal><HoverCard.Content className={styles.infoPopover} side="bottom" align="end" sideOffset={4} onPointerDownOutside={t22}>{t39}{t40}{t41}</HoverCard.Content></HoverCard.Portal>;
    $[102] = t39;
    $[103] = t40;
    $[104] = t42;
  } else {
    t42 = $[104];
  }
  let t43;
  if ($[105] !== infoOpen || $[106] !== t18 || $[107] !== t21 || $[108] !== t42) {
    t43 = <HoverCard.Root openDelay={150} closeDelay={300} open={infoOpen} onOpenChange={t18}>{t21}{t42}</HoverCard.Root>;
    $[105] = infoOpen;
    $[106] = t18;
    $[107] = t21;
    $[108] = t42;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  let t44;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <View size={12} />;
    $[110] = t44;
  } else {
    t44 = $[110];
  }
  let t45;
  if ($[111] !== labels.resetView || $[112] !== onReset) {
    t45 = <ButtonComponent className={styles.controlButton} onClick={onReset} aria-label={labels.resetView} title={labels.resetView} variant="outline">{t44}</ButtonComponent>;
    $[111] = labels.resetView;
    $[112] = onReset;
    $[113] = t45;
  } else {
    t45 = $[113];
  }
  let t46;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <ZoomIn size={12} />;
    $[114] = t46;
  } else {
    t46 = $[114];
  }
  let t47;
  if ($[115] !== labels.zoomIn || $[116] !== onZoomIn) {
    t47 = <ButtonComponent className={styles.controlButton} onClick={onZoomIn} aria-label={labels.zoomIn} title={labels.zoomIn} variant="outline">{t46}</ButtonComponent>;
    $[115] = labels.zoomIn;
    $[116] = onZoomIn;
    $[117] = t47;
  } else {
    t47 = $[117];
  }
  let t48;
  if ($[118] !== zoomPercentage) {
    t48 = zoomPercentage !== undefined && <span className={styles.zoomPercentage} aria-live="polite">{Math.round(zoomPercentage)}%</span>;
    $[118] = zoomPercentage;
    $[119] = t48;
  } else {
    t48 = $[119];
  }
  let t49;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <ZoomOut size={12} />;
    $[120] = t49;
  } else {
    t49 = $[120];
  }
  let t50;
  if ($[121] !== labels.zoomOut || $[122] !== onZoomOut) {
    t50 = <ButtonComponent className={styles.controlButton} onClick={onZoomOut} aria-label={labels.zoomOut} title={labels.zoomOut} variant="outline">{t49}</ButtonComponent>;
    $[121] = labels.zoomOut;
    $[122] = onZoomOut;
    $[123] = t50;
  } else {
    t50 = $[123];
  }
  let t51;
  if ($[124] !== t45 || $[125] !== t47 || $[126] !== t48 || $[127] !== t50) {
    t51 = <div className={styles.zoomGroup}>{t45}{t47}{t48}{t50}</div>;
    $[124] = t45;
    $[125] = t47;
    $[126] = t48;
    $[127] = t50;
    $[128] = t51;
  } else {
    t51 = $[128];
  }
  let t52;
  if ($[129] !== labels.textualAlternative || $[130] !== onTextualAlternative) {
    t52 = onTextualAlternative && <><button type="button" className={clsx("graph__textual-alternative", styles.textualAlternativeButton)} onClick={onTextualAlternative} title={labels.textualAlternative} aria-label={labels.textualAlternative}><Info role="img" aria-hidden={true} className="graph__textual-alternative__icon" />{" "}<span>{labels.textualAlternative}</span></button></>;
    $[129] = labels.textualAlternative;
    $[130] = onTextualAlternative;
    $[131] = t52;
  } else {
    t52 = $[131];
  }
  let t53;
  if ($[132] !== t13 || $[133] !== t17 || $[134] !== t43 || $[135] !== t5 || $[136] !== t51 || $[137] !== t52 || $[138] !== t9) {
    t53 = <div className={styles.container}>{t5}{t9}{t13}{t17}{t43}{t51}{t52}</div>;
    $[132] = t13;
    $[133] = t17;
    $[134] = t43;
    $[135] = t5;
    $[136] = t51;
    $[137] = t52;
    $[138] = t9;
    $[139] = t53;
  } else {
    t53 = $[139];
  }
  return t53;
}
function _temp2(prev) {
  return !prev;
}
function _temp(event, action) {
  if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") {
    return;
  }
  event.preventDefault();
  action();
}